import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from '@mui/material';
import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { upsertPpwrManualReportRequest } from 'redux/reducers/ppwr/reducer';
import { TPpwrManualReportingCountry } from 'redux/reducers/ppwr/types';
import { getAllCountriesOptions } from 'redux/reducers/settings/selectors';
import { CountryFlag } from 'ui/CountryFlag';
import { COLORS } from 'services/constants/colors';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

type TEditParcelsCountDialogProps = {
	rows: TPpwrManualReportingCountry[];
	// Index of the row being edited, `null` when the dialog is closed.
	index: number | null;
	setIndex: (index: number) => void;
	onClose: () => void;
};

const isValidParcelsCount = (value: string) => /^\d+$/.test(value.trim());

export const EditParcelsCountDialog: FC<TEditParcelsCountDialogProps> = ({
	rows,
	index,
	setIndex,
	onClose,
}) => {
	const dispatch = useDispatch();
	const countries = useSelector(getAllCountriesOptions);
	const inputRef = useRef<HTMLInputElement | null>(null);

	const row = index !== null ? rows[index] : null;
	const countryCode = row?.countryCode ?? '';
	const countryName = countries?.find((country) => country.code === countryCode)?.label;

	const [value, setValue] = useState('');

	// Load the value of the newly selected row and focus the field, so the user
	// can type a number and press Enter row after row.
	useEffect(() => {
		if (!row) return;
		setValue(String(row.parcelsCount ?? 0));
		requestAnimationFrame(() => inputRef.current?.select());
	}, [row]);

	const isValid = isValidParcelsCount(value);
	const hasPrevious = index !== null && index > 0;
	const hasNext = index !== null && index < rows.length - 1;

	const handleSave = () => {
		if (!row?.countryCode || !isValid) return;
		const parcelsCount = Number(value.trim());
		if (parcelsCount !== row.parcelsCount)
			dispatch(upsertPpwrManualReportRequest({ countryCode: row.countryCode, parcelsCount }));
	};

	const handleButtonSave = () => {
		handleSave();
		onClose();
	};

	const goPrevious = () => {
		handleSave();
		if (hasPrevious) setIndex(index - 1);
	};

	const goNext = () => {
		handleSave();
		if (hasNext) setIndex(index + 1);
		else onClose();
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter') return;
		event.preventDefault();
		goNext();
	};

	return (
		<Dialog open={!!row} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ color: COLORS.PRIMARY, fontWeight: 'bold' }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{countryCode && <CountryFlag countryCode={countryCode} height={18} />}
					{countryName ?? countryCode}
					{countryName && (
						<Typography component="span" sx={{ color: COLORS.PRIMARY }}>
							({countryCode})
						</Typography>
					)}
				</Box>
			</DialogTitle>
			<DialogContent>
				<TextField
					inputRef={inputRef}
					label="Parcels Count"
					value={value}
					onChange={(event) => setValue(event.target.value)}
					onKeyDown={handleKeyDown}
					error={!isValid}
					helperText={
						isValid
							? 'Press Enter to save and go to the next country'
							: 'Enter a whole number, 0 or more'
					}
					inputProps={{ inputMode: 'numeric' }}
					autoFocus
					fullWidth
					sx={{ mt: 1 }}
				/>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
				<Button variant="outlined" onClick={onClose}>
					Cancel
				</Button>

				<Box sx={{ display: 'flex', gap: 1 }}>
					<Button variant="outlined" onClick={goPrevious} disabled={!hasPrevious}>
						<ArrowBack sx={{ mr: 1 }} />
						Save and Go to the Previous
					</Button>
					<Button variant="outlined" onClick={handleButtonSave}>
						Save and Close
					</Button>

					<Button variant="contained" onClick={() => hasNext && goNext()} disabled={!hasNext}>
						Save and Go to the Next <ArrowForward sx={{ ml: 1 }} />
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
};
